import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, Trash2, Copy, Plus, FileText } from "lucide-react";
import { api, errMsg } from "../../lib/api";

function Refs({ refs }) {
  if (!refs?.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="ai-message-refs">
      {refs.map((r) => (
        <Link key={r.tag} to={r.path} className="inline-flex items-center gap-1 text-xs border border-sky-200 bg-sky-50 text-sky-800 rounded px-2 py-1 hover:bg-sky-100" data-testid={`ai-ref-${r.tag}`}>
          <FileText className="w-3 h-3" />[{r.tag}] {r.type}{r.ref ? ` ${r.ref}` : ""} · {r.title}{r.revision ? ` · rev ${r.revision}` : ""}
        </Link>
      ))}
    </div>
  );
}

function Message({ m }) {
  const mine = m.role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : ""}`}>
      <div className={`max-w-3xl rounded-xl px-4 py-3 text-sm whitespace-pre-wrap ${mine ? "bg-[#0A192F] text-white" : "bg-white border border-slate-200"}`} data-testid={mine ? "ai-user-message" : "ai-assistant-message"}>
        {m.content}
        {!mine && <Refs refs={m.refs} />}
        {!mine && <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-400"><button onClick={() => navigator.clipboard.writeText(m.content).then(() => toast.success("Copied"))} className="inline-flex items-center gap-1 hover:text-slate-700" data-testid="ai-copy-btn"><Copy className="w-3 h-3" />Copy</button><span>AI draft — review before use. Changes to records must be made and approved by an authorised person.</span></div>}
      </div>
    </div>
  );
}

export default function QordelAI() {
  const [st, setSt] = useState(null);
  const [convs, setConvs] = useState([]);
  const [cur, setCur] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const loadConvs = () => api.get("/ai/conversations").then((r) => setConvs(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/ai/settings").then((r) => setSt(r.data)).catch(() => setSt({ can_use: false }));
    loadConvs();
  }, []);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [cur]);
  const open = (id) => api.get(`/ai/conversations/${id}`).then((r) => setCur(r.data));
  const remove = async (id) => {
    await api.delete(`/ai/conversations/${id}`);
    if (cur?.id === id) setCur(null);
    loadConvs();
  };
  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setBusy(true);
    setText("");
    setCur((c) => ({ ...(c || {}), messages: [...(c?.messages || []), { role: "user", content: message }] }));
    try {
      const { data } = await api.post("/ai/chat", { message, conversation_id: cur?.id || null });
      await open(data.conversation_id);
      loadConvs();
    } catch (e) {
      toast.error(errMsg(e));
      setCur((c) => ({ ...c, messages: c.messages.slice(0, -1) }));
      setText(message);
    }
    setBusy(false);
  };
  if (!st) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="grid lg:grid-cols-[16rem_1fr] gap-6 h-[calc(100vh-9rem)]" data-testid="qordel-ai-page">
      <aside className="hidden lg:flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden">
        <button disabled={!st.can_use} onClick={() => setCur(null)} className="m-3 inline-flex items-center justify-center gap-2 bg-sky-600 disabled:bg-slate-300 text-white text-sm font-semibold py-2 rounded-md" data-testid="ai-new-chat-btn"><Plus className="w-4 h-4" />New conversation</button>
        <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
          {convs.map((c) => (
            <div key={c.id} className={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-sm cursor-pointer ${cur?.id === c.id ? "bg-sky-50 text-sky-900" : "hover:bg-slate-50"}`} onClick={() => open(c.id)} data-testid={`ai-conversation-${c.id}`}>
              <span className="flex-1 truncate">{c.title}</span>
              <button onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600" data-testid={`ai-conversation-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {!convs.length && <p className="px-2 text-xs text-slate-500">No conversations yet.</p>}
        </div>
      </aside>
      <section className="flex flex-col min-h-0">
        <div className="flex items-center gap-2 mb-3"><Sparkles className="w-5 h-5 text-sky-600" /><h1 className="text-xl font-bold">QORDEL AI</h1><span className="text-xs text-slate-500">Quality, safety, environmental &amp; compliance assistant</span></div>
        {!st.can_use && <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm rounded-xl p-4" data-testid="ai-unavailable">{st.can_use_reason || "QORDEL AI isn't set up for your organisation yet."} <Link to="/app/settings?tab=ai" className="underline font-semibold">Open AI settings</Link></div>}
        <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="ai-messages">
          {!cur?.messages?.length && st.can_use && (
            <div className="grid sm:grid-cols-2 gap-3 max-w-3xl" data-testid="ai-suggestions">
              {st.suggestions.map((s) => <button key={s} onClick={() => send(s)} className="text-left text-sm bg-white border border-slate-200 hover:border-sky-300 rounded-xl p-4" data-testid="ai-suggestion">{s}</button>)}
            </div>
          )}
          {(cur?.messages || []).map((m, i) => <Message key={i} m={m} />)}
          {busy && <p className="text-sm text-slate-500" data-testid="ai-thinking">QORDEL AI is reviewing your records…</p>}
          <div ref={end} />
        </div>
        {st.can_use && (
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
            <textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ask about your NCRs, audits, risks, documents…" className="field flex-1 resize-none" maxLength={6000} data-testid="ai-input" />
            <button disabled={busy || !text.trim()} className="bg-sky-600 disabled:bg-slate-300 text-white rounded-md px-4" data-testid="ai-send-btn"><Send className="w-4 h-4" /></button>
          </form>
        )}
        <p className="mt-2 text-[11px] text-slate-400">Answers use your organisation's QORDEL 360 records and may be wrong. Usage is billed to your organisation by Anthropic.</p>
      </section>
    </div>
  );
}
